// @flow
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import * as React from 'react';
import { Box, Button, Text } from 'grommet';
import { snakeCase, get, isEmpty } from 'lodash';
import {
    defineMessages, FormattedMessage, injectIntl, IntlShape,
} from 'react-intl';
import type { ApiClient } from 'speed-js-core';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import type { ValueType } from 'react-select/src/types';
import * as Yup from 'yup';
import { FieldWrapper } from 'speed-js-react-web';
import BaseSelect from 'esm-web-pro/components/forms/fields/BaseSelect';
import { withTheme } from 'styled-components';
import ListSelect from 'speed-js-react-web/src/components/fields/ListSelect';
import TextInput from 'speed-js-react-web/src/components/fields/TextInput';
// import TextInput from 'speed-js-react-web/src/components/grommet2/forms/fields/TextInput';
import RenderNewAddressSidePanel from 'esm-web-pro/components/forms/fields/RenderNewAddressSidePanel';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import { bindActionCreators } from 'redux';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import { connect } from 'react-redux';

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

type SidePanelActionProps = {
    openPanel: (key: string, url: ?string) => { key: string, url: string },
    closePanel: (key: string) => { key: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    field: {
        onChange: (e: SyntheticInputEvent<number>) => void,
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        value: any;
        name: string;
    },
    form: {
        setFieldValue: (string, any, ?boolean) => void,
    },
    required: boolean,
    label: string,
    help_text: string,
    allow_new: boolean,
    filters: Object,
    method: string,
    getAddressInitial: (objectUuid: string) => Promise<Object>,
    getStreetNumbers: (query: Object) => Promise<Object>,
    getBlocks: (query: Object) => Promise<Object>,
    theme: Object,
    onNewAddressSave?: Function,
    disabled?: boolean,
    intl: IntlShape,
    addNewFullForm?: boolean,
    requestRegion?: boolean,
    onRegionChange?: (region: Object) => void,
    oldAddressTitle?: string | null,
}

type State = {
    initialTownId: ?string,
    tType: ?string,
    townId: ?string,
    ekatte: ?string,
    townRegions: Array<Object>,
    regionId: ?string,
    qName: ?string,
    qType: ?string,
    strType: ?string,
    strName: ?string,
    strN: ?string,
    blockN: ?string,
    entrN: ?string,
    floorN: ?string,
    appN: ?string,
    postCode: ?string,
    status: ?string,
    hasEntrances: boolean,
    hasFloors: boolean,
    hasApartments: boolean,
    addingNew: boolean,
    strSubN: string,
    newAddress: Object,
    newAddressTown: string,
    newAddressDescription: string,
}

const messages = defineMessages({
    required: {
        id: 'addresspicker-required',
        defaultMessage: 'This field cannot be empty!',
    },
});

const NEW_ADDRESS_PANEL_KEY = 'NEW_ADDRESS_PANEL_KEY';

const menuContextValue = {
    menuPlacement: undefined,
    menuPortalTarget: undefined,
};

@withTheme
@connectAPI((apiClient: ApiClient) => ({
    getAddressInitial: {
        send: (objectUuid) => apiClient.resources.address.get(objectUuid),
        success: (initial: Object) => ({ initial, loading: true }),
    },
    getStreetNumbers: {
        send: (query: Object) => apiClient.resources.address.listStreetNumbers(query),
        success: (streetNumbers: Object) => ({ streetNumbers }),
    },
    getBlocks: {
        send: (query: Object) => apiClient.resources.address.listBlocks(query),
        success: (blocks: Object) => ({ blocks }),
    },
}))
@connect(null, mapDispatchToProps)
class AddressPicker extends React.Component<Props, State> {
    streetNumberRef: {
        current: BaseSelect,
    };

    blockNumberRef: {
        current: BaseSelect,
    };

    entranceRef: {
        current: BaseSelect,
    };

    addressRef: {
        current: BaseSelect,
    };

    listSelectStyles: Object;

    mounted: boolean;

    static defaultProps = {
        allow_new: false,
        filters: {},
        method: 'list',
        onNewAddressSave: undefined,
        disabled: false,
        addNewFullForm: true,
        requestRegion: false,
        onRegionChange: undefined,
        oldAddressTitle: null,
    };

    state = {
        initialTownId: undefined,
        tType: null,
        townId: null,
        townRegions: null,
        regionId: null,
        qType: null,
        qName: null,
        strType: null,
        strName: null,
        strN: null,
        strSubN: null,
        blockN: null,
        entrN: null,
        floorN: '',
        appN: '',
        postCode: null,
        status: null,
        hasEntrances: false,
        hasFloors: false,
        hasApartments: false,
        addingNew: false,
        newAddress: null,
        isDisabled: true,
    };

    static getValidationSchema(field: { validation: Object }) {
        let errMsg = Yup.string().nullable();
        if (field.validation.required) {
            errMsg = Yup.string().required(messages.required).nullable();
        }

        return errMsg;
    }

    constructor(props: Props) {
        super(props);

        this.streetNumberRef = React.createRef();
        this.blockNumberRef = React.createRef();
        this.entranceRef = React.createRef();
        this.addressRef = React.createRef();
        this.formikRef = React.createRef(null);
        this.isInitialMount = true;

        const { theme } = props;
        this.listSelectStyles = {};
        Object.entries(theme.listSelect.default).forEach(([styleKey, styleValue]) => {
            this.listSelectStyles[styleKey] = (provided, state) => ({
                ...provided,
                ...styleValue,
            });
        });
    }


    componentDidMount() {
        const { form, field, getAddressInitial } = this.props;

        this.mounted = true;

        const initialValue = get(form.values, field.name);
        if (initialValue) {
            // An ugly hack but the fastest for something that will be temporary in transforming wf contact details
            if (typeof initialValue === 'object') {
                this.setInitialDataFromExternalSource(initialValue);
            } else {
                getAddressInitial(initialValue).then((res) => {
                    if (this.mounted) {
                        form.setFieldValue(field.name, res);
                        this.setState({
                            initialTownId: undefined,
                            tType: res.t_type,
                            townId: res.town_id,
                            ekatte: res.ekatte,
                            newAddressTown: res.town_id,
                            qName: res.q_name,
                            qType: res.q_type,
                            strType: res.str_type,
                            strName: res.str_name,
                            strN: res.str_subn ? res.str_n.concat('-', res.str_subn) : res.str_n,
                            strSubN: res.str_subn,
                            blockN: res.block_n,
                            entrN: res.entr_n,
                            floorN: res.floor_n,
                            appN: res.app_n,
                            postCode: res.post_code,
                            status: res.status,
                        });
                        this.checkHasEntrancesAndFloors(res);
                    }
                });
            }
        }
    }

    componentWillUnmount(): void {
        this.mounted = false;
    }

    setInitialDataFromExternalSource(value) {
        this.setState((prev) => ({
            ...prev,
            initialTownId: value.townId,
            newAddressTown: value.townId,
            ...value,
        }), () => {
            this.checkHasEntrancesAndFloors(this.state);
        });
    }

    formatAddress = (addr: Object) => {
        let result = `${addr.town_id}`;

        if (addr.t_type) {
            result = `${addr.t_type} ${result}`;
        }

        if (!addr.str_name) {
            result = `${result} ${addr.q_type || ''}`.trim();
            result = `${result} ${addr.q_name || ''}`.trim();
        } else {
            result = `${result} ${addr.str_type || ''}`.trim();
            result = `${result} ${addr.str_name || ''}`.trim();
            result = `${result} ${addr.str_n || ''}${addr.str_subn || ''}`.trim();
        }

        if (addr.block_n) {
            // TODO: i18n
            result = `${result} БЛОК ${addr.block_n}`;
        }

        if (addr.entr_n) {
            result = `${result} ВХ. ${addr.entr_n}`;
        }

        return result;
    };

    handleTownChange = (value: string, _original: Object) => {
        const { onRegionChange } = this.props;

        let regionId = null;

        if (onRegionChange) {
            if (_original && _original.region) {
                const townRegionLength = Object.keys(_original.region).length;
                if (townRegionLength === 0) {
                    // No regions
                    onRegionChange(null);
                } else if (townRegionLength === 1) {
                    // Only one region, pre-select it
                    regionId = _original.region[0].id;
                    if (onRegionChange) {
                        onRegionChange(_original.region[0]);
                    }
                }
            } else {
                onRegionChange(null);
            }
        }

        this.setState({
            initialTownId: undefined,
            townId: value,
            newAddressTown: value,
            // Only set the townRegions state if there are more than 1 region and the user has to pick among them
            townRegions: _original && _original.region?.length > 1 ? _original.region : null,
            regionId,
            qType: null,
            ekatte: _original && _original.ekatte,
            qName: null,
            strType: null,
            strName: null,
            strN: null,
            strSubN: null,
            blockN: null,
            entrN: null,
            floorN: '',
            appN: '',
            postCode: null,
            status: null,
            hasEntrances: false,
            hasFloors: false,
            hasApartments: false,
        }, () => {
            const { form: { setFieldValue }, field: { name, value: fValue } } = this.props;

            if (fValue) {
                setFieldValue(name, null);
            }
        });
    };

    handleRegionChange = (value: string) => {
        const { onRegionChange } = this.props;
        const { townRegions } = this.state;
        if (onRegionChange) {
            onRegionChange(townRegions?.find((tr) => `${tr.id}` === value));
        }

        this.setState({
            regionId: value,
            qType: null,
            qName: null,
            strType: null,
            strName: null,
            strN: null,
            strSubN: null,
            blockN: null,
            entrN: null,
            floorN: '',
            appN: '',
            postCode: null,
            status: null,
            hasEntrances: false,
            hasFloors: false,
            hasApartments: false,
        });
    };

    handleQuarterChange = (value: string, _original: Object) => {
        this.setState({
            qType: _original?.q_type,
            qName: value,
            strType: null,
            strName: null,
            strN: null,
            strSubN: null,
            blockN: null,
            entrN: null,
            floorN: '',
            appN: '',
            postCode: null,
            status: null,
            hasEntrances: false,
            hasFloors: false,
            hasApartments: false,
        }, () => {
            const { form: { setFieldValue }, field: { name, value: fValue } } = this.props;
            if (fValue) {
                setFieldValue(name, null);
            }
        });
    };

    handleStreetChange = (value: string, _original: Object) => {
        this.setState({
            strType: _original?.str_type,
            strName: value,
            strN: null,
            strSubN: null,
            blockN: null,
            entrN: null,
            floorN: '',
            appN: '',
            postCode: null,
            status: null,
            hasEntrances: false,
            hasFloors: false,
            hasApartments: false,
        }, () => {
            const { form: { setFieldValue }, field: { name, value: fValue } } = this.props;
            if (fValue) {
                setFieldValue(name, null);
            }
        });
    };

    handleStreetNChange = (value: string) => {
        let hasEntrances = false;
        let hasFloors = false;
        let hasApartments = false;
        let strSubN = null;

        if (value) {
            const option = this.streetNumberRef.current.getValue(value, this.streetNumberRef.current.state.options);
            strSubN = option._original.str_subn;
            hasEntrances = option._original.has_entr_n;
            hasFloors = option._original.has_floor_n;
            hasApartments = option._original.has_app_n;
        }

        this.setState({
            strN: value,
            strSubN,
            blockN: null,
            entrN: null,
            floorN: '',
            appN: '',
            postCode: null,
            status: null,
            hasEntrances,
            hasFloors,
            hasApartments,
        }, () => {
            const { form: { setFieldValue }, field: { name, value: fValue } } = this.props;
            if (fValue) {
                setFieldValue(name, null);
            }
        });
    };

    handleBlockNChange = (value: string) => {
        let hasEntrances = false;
        let hasFloors = false;
        let hasApartments = false;
        if (value) {
            const option = this.blockNumberRef.current.getValue(value, this.blockNumberRef.current.state.options);
            hasEntrances = option._original.has_entr_n;
            hasFloors = option._original.has_floor_n;
            hasApartments = option._original.has_app_n;
        }

        this.setState({
            blockN: value,
            entrN: null,
            floorN: '',
            appN: '',
            postCode: null,
            status: null,
            hasEntrances,
            hasFloors,
            hasApartments,
        }, () => {
            const { form: { setFieldValue }, field: { name, value: fValue } } = this.props;
            if (fValue) {
                setFieldValue(name, null);
            }
        });
    };

    handleEntrNChange = (value: string) => {
        this.setState({
            entrN: value,
            floorN: '',
            appN: '',
            postCode: null,
            status: null,
        }, () => {
            const { form: { setFieldValue }, field: { name, value: fValue } } = this.props;
            if (fValue) {
                setFieldValue(name, null);
            }
        });
    };

    handleFloorNChange = (value: string) => {
        const { form: { setFieldValue }, field: { name, value: fValue } } = this.props;

        this.setState({
            floorN: value,
        });

        if (fValue) {
            fValue.floor_n = value;
            setFieldValue(name, fValue);
        }
    };

    handleAppartmentNChange = (value: string) => {
        const { form: { setFieldValue }, field: { name, value: fValue } } = this.props;

        this.setState({
            appN: value,
        });

        if (fValue) {
            fValue.app_n = value;
            setFieldValue(name, fValue);
        }
    };

    checkHasEntrancesAndFloors = (value: Object) => {
        const {
            getStreetNumbers, getBlocks,
        } = this.props;

        if (value.str_n) {
            // Check whether the chosen address has entrances or floors
            getStreetNumbers({
                town_id: value.town_id,
                q_name: value.q_name,
                str_name: value.str_name,
                str_n: value.str_n,
            }).then((response) => {
                if (response.results.length > 0) {
                    this.setState({
                        hasEntrances: response.results[0].has_entr_n,
                        hasFloors: response.results[0].has_floor_n,
                        hasApartments: response.results[0].has_app_n,
                    });
                }
            });
        } else if (value.block_n) {
            // Check based on the street number and the block number
            getBlocks({
                town_id: value.town_id,
                q_name: value.q_name,
                str_name: value.str_name,
                str_n: value.str_n,
                block_n: value.block_n,
            }).then((response) => {
                if (response.results.length > 0) {
                    this.setState({
                        hasEntrances: response.results[0].has_entr_n,
                        hasFloors: response.results[0].has_floor_n,
                        hasApartments: response.results[0].has_app_n,
                    });
                }
            });
        }
    };

    handleHorizontalAddressChange = (value: ValueType, _original: Object) => {
        const {
            form: { setFieldValue }, field: { name },
        } = this.props;
        const fieldValue = { ..._original };
        const { appN, floorN } = this.state;
        fieldValue.app_n = appN;
        fieldValue.floor_n = floorN;
        setFieldValue(name, fieldValue);

        // Fill other fields with the info of the chosen address
        this.setState({
            tType: _original.t_type,
            townId: _original.town_id,
            ekatte: _original.ekatte,
            newAddressTown: _original.town_id,
            qName: _original.q_name,
            qType: _original.q_type,
            strType: _original.str_type,
            strName: _original.str_name,
            strN: _original.str_subn && _original.str_n
                ? _original.str_n.concat('-', _original.str_subn)
                : _original.str_n,
            strSubN: _original.str_subn,
            blockN: _original.block_n,
            entrN: _original.entr_n,
            postCode: _original.post_code,
            status: _original.status,
        });

        this.checkHasEntrancesAndFloors(_original);
    };

    handleOptionsLoad = (options: { value: string, label: string, _original: Object }[]) => {
        if (options.length === 1 && this.isInitialMount) {
            // $FlowIgnore
            this.handleHorizontalAddressChange(options[0].value, options[0]._original);
            this.isInitialMount = false;
        }
    }

    handleNewAddressClick = (e: SyntheticMouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        this.setState({
            addingNew: true,
            newAddressDescription: null,
        });
    };

    handleNewAddressClose = () => {
        const { closePanel } = this.props;
        closePanel(NEW_ADDRESS_PANEL_KEY);
        this.setState({
            addingNew: false,
            newAddressDescription: null,
        });
    };

    handleNewAddressSubmit = (values: Object) => {
        const {
            form: { setFieldValue }, field: { name }, onNewAddressSave, addNewFullForm,
        } = this.props;
        if (addNewFullForm) {
            setFieldValue(name, values, false);

            const formattedValues = [
                values.t_type?.trim(),
                values.town_id?.trim(),
                values.q_type?.trim(),
                values.q_name?.trim(),
                values.str_type?.trim(),
                values.str_name?.trim(),
                (values.str_subn ? values.str_n?.concat('-', values.str_subn) : values.str_n)?.trim(),
                values.block_n?.trim() ? `БЛОК ${values.block_n?.trim()}` : '',
                values.entr_n?.trim() ? `ВХ. ${values.entr_n?.trim()}` : '',
                values.floor_n?.trim() ? `ЕТ. ${values.floor_n?.trim()}` : '',
                values.app_n?.trim() ? `АП. ${values.app_n?.trim()}` : '',
            ];

            // Fill other fields with the info of the chosen address
            this.setState({
                source: 'esm-web-pro',
                id: null,
                uuid: null,
                tType: values.t_type,
                townId: values.town_id,
                newAddressTown: values.town_id,
                qName: values.q_name,
                qType: values.q_type,
                strType: values.str_type,
                strName: values.str_name,
                strN: values.str_subn ? values.str_n.concat('-', values.str_subn) : values.str_n,
                strSubN: values.str_subn,
                blockN: values.block_n,
                entrN: values.entr_n,
                postCode: values.post_code,
                status: values.status,
                addingNew: false,
                floorN: values.floor_n,
                appN: values.app_n,
                newAddress: {
                    ...values,
                    name_formatted: formattedValues.filter((v) => !!v).join(' '),
                },
            });

            if (onNewAddressSave) {
                onNewAddressSave(values);
            }
        } else {
            const { newAddressTown, newAddressDescription } = this.state;
            const newAddress = {
                status: 'pending',
                town_id: newAddressTown,
                name_formatted: `гр. ${newAddressTown}, ${newAddressDescription}`,
            };
            setFieldValue(name, newAddress, false);

            this.setState({
                addingNew: false,
                newAddress,
            });
            if (onNewAddressSave) {
                onNewAddressSave(newAddress);
            }
        }
    };

    noOptionsMessage = () => (
        <FormattedMessage
            id="addresspicker-no-options"
            defaultMessage="No options"
        />
    )

    addNewAddressButton = () => {
        const { allow_new: allowNew } = this.props;
        if (allowNew) {
            return (
                <Button
                    label={(
                        <FormattedMessage
                            id="addresspicker-btn-findaddress"
                            defaultMessage="Don't find your address?"
                        />
                    )}
                    onClick={this.handleNewAddressClick}
                    margin={{ top: 'small', bottom: 'small' }}
                    style={{ width: '100%' }}
                />
            );
        }
        return this.noOptionsMessage();
    };

    renderRegion = () => {
        const { requestRegion } = this.props;
        const { townId, townRegions, regionId } = this.state;
        if (!townId || !requestRegion || isEmpty(townRegions)) return null;

        const { field: { name } } = this.props;

        const regionChoices = {};
        townRegions.forEach((tr) => {
            regionChoices[tr.id] = tr.name;
        });

        return (
            <React.Fragment>
                <Box pad={{ top: 'small' }}>
                    <Text as="label">
                        <FormattedMessage id="addresses-region" defaultMessage="Region" />
                    </Text>
                </Box>
                <Box pad={{ top: 'xsmall' }}>
                    <BaseSelect
                        choices={regionChoices}
                        onChange={this.handleRegionChange}
                        name={`${name}-region_id`}
                        value={regionId}
                        emptyLabel={<FormattedMessage id="addresses-region-name" defaultMessage="Region name" />}
                    />
                </Box>
            </React.Fragment>
        );
    };

    renderStreetQuarter = () => {
        const { requestRegion } = this.props;
        const { townId, regionId, townRegions } = this.state;
        if (!townId || (requestRegion && !regionId && !isEmpty(townRegions))) return null;

        const { field: { name } } = this.props;
        const {
            qName, qType, strName, strType, strN,
        } = this.state;

        return (
            <React.Fragment>
                <Box pad={{ top: 'small' }}>
                    <Text as="label">
                        <FormattedMessage id="addresses-quarter" defaultMessage="Quarter" />
                    </Text>
                </Box>
                <Box pad={{ top: 'xsmall' }}>
                    <BaseSelect
                        onChange={this.handleQuarterChange}
                        name={`${name}-q_name`}
                        value={qName}
                        valueTypePK={false}
                        resourceName="quarter"
                        valueField="name"
                        searchField="name__icontains"
                        labelFields={['type', 'name']}
                        emptyLabel={<FormattedMessage id="addresses-quartername" defaultMessage="Quarter name" />}
                        minSearchLength={0}
                        initialSearchText={qName}
                        preload
                        additionalValueFilters={{ type: qType }}
                        additionalFilters={{ city__name: townId }}
                        noOptionsMessage={this.addNewAddressButton}
                    />
                </Box>
                <Box pad={{ top: 'small' }}>
                    <Text as="label">
                        <FormattedMessage id="addresses-street" defaultMessage="Street" />
                    </Text>
                </Box>
                <Box direction="row" pad={{ top: 'xsmall' }} fill="horizontal" gap="small">
                    <Box fill="horizontal">
                        <BaseSelect
                            onChange={this.handleStreetChange}
                            value={strName}
                            valueTypePK={false}
                            name={`${name}-str_name`}
                            resourceName="street"
                            valueField="name"
                            initialSearchText={strName}
                            labelFields={['type', 'name']}
                            emptyLabel={<FormattedMessage id="addresses-streetname" defaultMessage="Street name" />}
                            minSearchLength={0}
                            preload
                            additionalValueFilters={{ type: strType }}
                            additionalFilters={{ city__name: townId, quarters__name: qName }}
                            noOptionsMessage={this.addNewAddressButton}
                        />
                    </Box>
                    <Box fill="horizontal">
                        <BaseSelect
                            onChange={this.handleStreetNChange}
                            value={strN}
                            valueTypePK={false}
                            name={`${name}-str_n`}
                            resourceName="address"
                            resourceMethod="listStreetNumbers"
                            searchField="str_n__icontains"
                            valueField="str_n"
                            valueFields={['str_n', 'str_subn']}
                            labelFields={['str_n', 'str_subn']}
                            emptyLabel={(
                                <FormattedMessage
                                    id="addresses-streetnumber"
                                    defaultMessage="Street number"
                                />
                            )}
                            minSearchLength={0}
                            preload
                            additionalFilters={{ town_id: townId, q_name: qName, str_name: strName }}
                            ref={this.streetNumberRef}
                            noOptionsMessage={this.addNewAddressButton}
                            disabled={!strName}
                        />
                    </Box>
                </Box>
            </React.Fragment>
        );
    };

    getOriginalFieldVal = (modifiedVal) => {
        // If there is no sub number in the state return the value as is, since this is a valid number spanning
        // multiple numbers, e.g. 30-40
        if (!this.state.strSubN) {
            return modifiedVal;
        }
        if (!modifiedVal) {
            return modifiedVal;
        }
        const splitVal = modifiedVal.split('-');
        if (splitVal.length === 1) {
            return modifiedVal;
        }
        splitVal.splice(splitVal.length - 1, 1);
        return splitVal.join('-');
    };

    renderNumbers = () => {
        const { requestRegion } = this.props;
        const {
            townId, regionId, townRegions, qName, strName,
        } = this.state;

        if (!townId || (requestRegion && !regionId && !isEmpty(townRegions))) return null;

        const {
            field: { name, value }, method, allow_new: allowNew,
        } = this.props;

        const {
            strN, blockN, entrN, floorN, hasEntrances, hasFloors, hasApartments, strSubN, appN,
        } = this.state;

        return (
            <React.Fragment>
                <Box pad={{ top: 'small' }}>
                    <Text as="label">
                        <FormattedMessage id="addresses-building" defaultMessage="Building" />
                    </Text>
                </Box>
                <Box direction="row" pad={{ top: 'xsmall' }} fill="horizontal" gap="small">
                    {
                        <Box fill="horizontal">
                            <BaseSelect
                                onChange={this.handleBlockNChange}
                                value={blockN}
                                valueTypePK={false}
                                name={`${name}-block_n`}
                                resourceName="address"
                                resourceMethod="listBlocks"
                                searchField="block_n__icontains"
                                valueField="block_n"
                                labelFields={['block_n']}
                                emptyLabel={(
                                    <FormattedMessage
                                        id="addresses-buildingnumber"
                                        defaultMessage="Building number"
                                    />
                                )}
                                minSearchLength={0}
                                preload
                                additionalFilters={{
                                    town_id: townId,
                                    q_name: qName,
                                    str_name: strName,
                                    str_n: this.getOriginalFieldVal(strN),
                                }}
                                ref={this.blockNumberRef}
                                noOptionsMessage={this.addNewAddressButton}
                                disabled={!(strName || qName)}
                            />
                        </Box>
                    }
                    {
                        <Box fill="horizontal">
                            <BaseSelect
                                onChange={this.handleEntrNChange}
                                value={entrN}
                                valueTypePK={false}
                                name={`${name}-entr_n`}
                                resourceName="address"
                                resourceMethod="listEntrances"
                                searchField="entr_n__icontains"
                                valueField="entr_n"
                                labelFields={['entr_n']}
                                emptyLabel={(
                                    <FormattedMessage
                                        id="addresses-entrancenumber"
                                        defaultMessage="Entrance number"
                                    />
                                )}
                                minSearchLength={0}
                                preload
                                additionalFilters={{
                                    town_id: townId, q_name: qName, str_name: strName, str_n: strN, block_n: blockN,
                                }}
                                ref={this.entranceRef}
                                noOptionsMessage={this.addNewAddressButton}
                                disabled={!(strName || blockN)}
                            />
                        </Box>
                    }
                </Box>
                {qName || strName ? (
                    <>
                        <Box direction="column" pad={{ top: 'small' }}>
                            <ListSelect
                                onChange={this.handleHorizontalAddressChange}
                                onOptionsLoad={this.handleOptionsLoad}
                                value={value?.uuid}
                                valueTypePK={false}
                                initialObjectId={value?.uuid}
                                name={name}
                                resourceName="address"
                                resourceMethod={method || 'list'}
                                valueField="uuid"
                                resultsCountLabel={(
                                    <FormattedMessage
                                        id="addresspicker-addresses-found"
                                        defaultMessage="Addresses found"
                                    />
                                )}
                                minSearchLength={0}
                                preload
                                additionalFilters={{
                                    type: 'h',
                                    town_id: townId,
                                    q_name: qName,
                                    str_name: strName,
                                    str_n: this.getOriginalFieldVal(strN),
                                    str_subn: strSubN,
                                    block_n: blockN,
                                    entr_n: entrN,
                                }}
                                formatLabel={this.formatAddress}
                                ref={this.addressRef}
                                customSelectStyles={this.listSelectStyles}
                                noOptionsMessage={this.addNewAddressButton}
                            />
                        </Box>
                        <Box pad={{ top: 'small' }}>
                            <Text as="label">
                                <FormattedMessage id="addresses-vertical" defaultMessage="Vertical" />
                            </Text>
                        </Box>
                        <Box direction="row" pad={{ top: 'xsmall', bottom: 'small' }} fill="horizontal" gap="small">
                            <Box fill="horizontal">
                                <TextInput
                                    onChange={(e) => {
                                        this.handleFloorNChange(e.target?.value || '');
                                    }}
                                    value={floorN}
                                    name="floor"
                                />
                            </Box>
                            <Box fill="horizontal">
                                <TextInput
                                    onChange={(e) => {
                                        this.handleAppartmentNChange(e.target?.value || '');
                                    }}
                                    value={appN}
                                    name="apartment"
                                />
                            </Box>
                        </Box>
                    </>
                ) : null}
            </React.Fragment>
        );
    };

    renderNewAddressTownPicker = () => {
        const { newAddressTown } = this.state;

        return (
            <Box margin={{ bottom: 'small' }}>
                <FieldWrapper
                    required
                    label={(<FormattedMessage id="addresspicker-town-emptylabel" defaultMessage="City" />)}
                >
                    <BaseSelect
                        onChange={(value) => {
                            this.setState({
                                newAddressTown: value,
                            });
                        }}
                        value={newAddressTown}
                        choices={{
                            [newAddressTown]: newAddressTown,
                        }}
                        disabled
                    />
                </FieldWrapper>
            </Box>
        );
    };

    renderNewAddressTextInput = () => (
        <Box margin={{ bottom: 'small' }}>
            <FieldWrapper
                required
                label={(<FormattedMessage id="address" defaultMessage="Address" />)}
            >
                <TextInput
                    label={(<FormattedMessage id="address" defaultMessage="Address" />)}
                    onChange={(e) => {
                        this.setState({
                            newAddressDescription: e.target?.value || e,
                        });
                    }}
                    value={this.state.newAddressDescription}
                />
            </FieldWrapper>
        </Box>
    );


    renderNewAddressResult = () => {
        const { newAddress } = this.state;
        const { form: { setFieldValue }, field: { name }, intl: { formatMessage } } = this.props;

        const newAddressActions = [
            {
                key: 'edit-new-address',
                icon: <EditIcon size="small" />,
                tooltip: formatMessage(actionMessages.edit),
                radius: '0%',
                color: 'transparent',
                callback: () => this.setState({
                    newAddress: null,
                    townId: null,
                    townRegions: null,
                    regionId: null,
                    qType: null,
                    qName: null,
                    strType: null,
                    strName: null,
                    strN: null,
                    strSubN: null,
                    blockN: null,
                    entrN: null,
                    floorN: '',
                    appN: '',
                    postCode: null,
                    status: null,
                    hasEntrances: false,
                    hasFloors: false,
                    hasApartments: false,
                }, () => setFieldValue(name, null)),
            },
        ];

        return (
            <Box>
                <Box pad={{ horizontal: 'small' }} direction="row" align="center">
                    <Box width="90%">
                        <Text>
                            {newAddress.name_formatted}
                        </Text>
                    </Box>
                    <MainActions main actions={newAddressActions} />
                </Box>
            </Box>
        );
    };

    render() {
        const {
            // eslint-disable-next-line babel/camelcase
            field: { name, value }, required, label, help_text: help, filters, disabled, oldAddressTitle, allow_new,
            addNewFullForm,
        } = this.props;
        const {
            townId, newAddress, initialTownId, addingNew, townRegions, regionId, newAddressDescription,
            qType, qName, strName, strType, strN, strSubN, blockN, entrN, floorN, appN, postCode, status, hasEntrances,
            hasFloors, hasApartments, ekatte,
        } = this.state;

        // eslint-disable-next-line babel/camelcase
        const allow = allow_new;

        const initialData = {
            new_address: newAddress,
            town_id: townId || null,
            ekatte: ekatte || null,
            town_regions: townRegions,
            region_id: regionId,
            q_type: qType,
            q_name: qName,
            str_type: strType,
            str_name: strName,
            str_n: strN,
            str_subn: strSubN,
            block_n: blockN,
            entr_n: entrN,
            floor_n: floorN,
            app_n: appN,
            post_code: postCode,
            status,
            hasEntrances,
            hasFloors,
            hasApartments,
        };

        return (
            <FieldWrapper
                name={name}
                required={required}
                helpText={help}
                label={label}
            >
                <Box id={`${name}-container`}>
                    {!newAddress ? (
                        <>
                            <Box>
                                <Text as="label">
                                    <FormattedMessage id="addresspicker-town-emptylabel" defaultMessage="City" />
                                </Text>
                            </Box>
                            <Box pad={{ top: 'xsmall' }}>
                                <BaseSelect
                                    onChange={this.handleTownChange}
                                    value={townId}
                                    valueTypePK={false}
                                    initialSearchText={initialTownId}
                                    name={`${name}-town_id`}
                                    resourceName="city"
                                    valueField="name"
                                    labelFields={['name']}
                                    minSearchLength={0}
                                    preload
                                    emptyLabel={(
                                        <FormattedMessage
                                            id="addresspicker-town-emptylabel"
                                            defaultMessage="City"
                                        />
                                    )}
                                    additionalFilters={filters.city || { name__in: 'ПЛОВДИВ,СОФИЯ,ВЕЛИКО ТЪРНОВО' }}
                                    noOptionsMessage={this.noOptionsMessage}
                                    disabled={disabled}
                                />
                            </Box>
                            {this.renderRegion()}
                            {this.renderStreetQuarter()}
                            {this.renderNumbers()}
                            {addingNew && (
                                <RenderNewAddressSidePanel
                                    initialData={initialData}
                                    allow_new={allow}
                                    addingNew={addingNew}
                                    addressRef={this.addressRef}
                                    townId={townId}
                                    ekatte={ekatte}
                                    qName={qName}
                                    newAddressDescription={newAddressDescription}
                                    addNewFullForm={addNewFullForm}
                                    oldAddressTitle={oldAddressTitle}
                                    value={value}
                                    id={NEW_ADDRESS_PANEL_KEY}
                                    menuContextValue={menuContextValue}
                                    formikRef={this.formikRef}
                                    handleNewAddressSubmit={this.handleNewAddressSubmit}
                                    handleNewAddressClose={this.handleNewAddressClose}
                                    renderNewAddressTownPicker={this.renderNewAddressTownPicker()}
                                    renderRegion={this.renderRegion()}
                                    renderNewAddressTextInput={this.renderNewAddressTextInput()}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            {this.renderNewAddressResult()}
                        </>
                    )}
                </Box>
            </FieldWrapper>
        );
    }
}


export default injectIntl(AddressPicker);
